<script setup>
import { ref } from "vue";
import Swal from "sweetalert2";
import AuthLayout from "@/components/layouts/AuthLayout.vue";
import DOMPurify from "dompurify";

const email = ref("");
const isLoading = ref(false);

const sanitizeInput = (input) => {
  if (typeof input !== "string") return input;
  return DOMPurify.sanitize(input.trim());
};

const requestPasswordReset = async (email) => {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ email })
    });

    const data = await response.json();

    if (response.ok) {
      return { success: true, data };
    } else {
      throw { response: { data } };
    }
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

const onSubmit = async () => {
  if (!email.value || !email.value.includes("@")) {
    Swal.fire("Correo inválido", "Por favor ingresa un correo válido", "error");
    return;
  }

  isLoading.value = true;

  try {
    await requestPasswordReset(sanitizeInput(email.value.toLowerCase()));

    Swal.fire({
      icon: "success",
      title: "Correo enviado",
      text: "Se ha enviado un código de verificación a tu correo electrónico. Revisa tu bandeja de entrada.",
      confirmButtonColor: "#99b642",
    });
  } catch (error) {
    let errorMessage = "Error al enviar el código de verificación. Intenta nuevamente.";
    
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.data?.errors?.email) {
      errorMessage = error.response.data.errors.email[0];
    }
    
    Swal.fire("Error", errorMessage, "error");
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <AuthLayout>
    <template #title>
      <h1 class="text-4xl font-extrabold text-white mb-2 drop-shadow-lg">
        Restablecer contraseña
      </h1>
    </template>

    <form @submit.prevent="onSubmit" class="space-y-6 max-w-md mx-auto w-full">
      <div>
        <label class="text-white block mb-2">Correo electrónico</label>
        <input
          v-model="email"
          type="email"
          placeholder="Ingresa tu correo"
          class="w-full px-4 py-2 rounded-md bg-white text-black shadow"
          required
        />
      </div>

      <button
        type="submit"
        class="bg-[#99b642] hover:bg-[#88a534] text-white font-semibold py-2 px-4 rounded w-full"
        :disabled="isLoading"
      >
        <span v-if="isLoading" class="flex justify-center items-center">
          <div class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
          Enviando...
        </span>
        <span v-else>Enviar código de recuperación</span>
      </button>
    </form>

    <template #footer-links>
      <p class="text-center text-white mt-6">
        ¿Ya lo recibiste?
        <router-link
          to="/reset-password"
          class="font-semibold underline hover:text-[#d4e4a9] transition-colors ml-1"
        >
          Cambiar contraseña aquí
        </router-link>
      </p>
    </template>
  </AuthLayout>
</template>